import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { pdfjs } from 'react-pdf';
import { Route, Switch } from 'react-router-dom';
import { CssBaseline } from '@material-ui/core';
import { EntityProvider } from '@sierralabs/entity-ui';
import { defaultQueryBuilderPlugins, QueryProvider } from '@sierralabs/query-ui';
import { UserContext, UserProvider } from '@sierralabs/user-ui';
import { WorkflowProvider } from '@sierralabs/workflow-ui';
import { useComponentSubscriptions } from './hooks/component/useComponentSubscriptions';
import { useGetComponents } from './hooks/component/useGetComponents';
import { useSendSiteComponents } from './hooks/iframe-messaging/useSendSiteComponents';
import { useManageSiteDialog } from './hooks/site-dialog/useManageSiteDialog';
import { useFindSite } from './hooks/site/useFindSite';
import { useSiteConfigurator } from './hooks/useSiteConfigurator';
import { IframeMessageProvider, isIframe } from './IframeMessageContext';
import { defaultSitePlugins } from './plugins/components/default';
import { defaultSiteEventPlugins } from './plugins/events/default';
import { SiteLogin } from './plugins/login/SiteLoginPlugin';
import { defaultSiteParamPlugins } from './plugins/params/default';
import SiteAdminPage from './SiteAdminPage';
import { SiteThemeProvider } from './SiteThemeProvider';
// NOTE: taken from https://github.com/wojtekmaj/react-pdf/issues/321#issuecomment-451291757
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
export const SiteContext = createContext(null);
export const useSiteContext = () => {
    const context = useContext(SiteContext);
    if (!context) {
        throw new Error('useSiteContext must be used within the context of the SiteProvider component');
    }
    return context;
};
export const SiteProvider = props => {
    const { onContextChange } = props;
    const contextRef = useRef();
    return (React.createElement(IframeMessageProvider, { contextRef: contextRef },
        React.createElement(SiteContextProvider, Object.assign({}, props, { onContextChange: context => {
                contextRef.current = context;
                onContextChange === null || onContextChange === void 0 ? void 0 : onContextChange(context);
            } }))));
};
export const SiteContextProvider = ({ theme, hostname, editorPath = '/admin', children, plugins, eventPlugins, paramPlugins, formElementPlugins, queryBuilderPlugins, routePermissions, routeHasAccess: routeHasAccessProp, workflowPlugins, onContextChange }) => {
    const [updateComponentState, setUpdateComponentState] = useState({});
    const componentSubscriptions = useComponentSubscriptions();
    const getComponentsApi = useGetComponents();
    const siteConfigurator = useSiteConfigurator(Object.assign(Object.assign({}, componentSubscriptions), getComponentsApi));
    const manageSiteDialog = useManageSiteDialog(plugins || defaultSitePlugins, getComponentsApi.siteComponents);
    const routeHasAccessRef = useRef(routeHasAccessProp);
    routeHasAccessRef.current = routeHasAccessProp;
    const onContextChangeRef = useRef(onContextChange);
    const findSiteApi = useFindSite();
    const { sendSiteComponents } = useSendSiteComponents();
    editorPath = useMemo(() => editorPath[editorPath.length - 1] !== '/' ? `${editorPath}/` : editorPath, [editorPath]);
    const context = useMemo(() => (Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ editorPath, plugins: plugins || defaultSitePlugins, eventPlugins: eventPlugins || defaultSiteEventPlugins, paramPlugins: paramPlugins || defaultSiteParamPlugins, formElementPlugins, queryBuilderPlugins: queryBuilderPlugins || defaultQueryBuilderPlugins, routePermissions: routePermissions || [], routeHasAccess: routeHasAccessRef.current }, findSiteApi), siteConfigurator), componentSubscriptions), updateComponentState), getComponentsApi), manageSiteDialog), { updateComponentState,
        setUpdateComponentState })), [
        editorPath,
        eventPlugins,
        paramPlugins,
        formElementPlugins,
        queryBuilderPlugins,
        plugins,
        routePermissions,
        componentSubscriptions,
        updateComponentState,
        siteConfigurator,
        getComponentsApi,
        findSiteApi,
        manageSiteDialog
    ]);
    // notify context change
    useEffect(() => {
        var _a;
        (_a = onContextChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onContextChangeRef, context);
    }, [context]);
    const { editing, selectedRootComponent, site, setSite } = siteConfigurator;
    const { loadedSite, findSite, findSiteByDomain } = findSiteApi;
    // update siteId if site is loaded from backend
    useEffect(() => void (loadedSite && setSite(loadedSite)), [setSite, loadedSite]);
    // initial load of site entity
    useEffect(() => {
        if (window.location.pathname.indexOf(editorPath) !== 0 && !isIframe()) {
            // not editor so get site from domain and path
            const { pathname } = window.location;
            const host = hostname || window.location.hostname;
            findSiteByDomain(host, pathname);
        }
    }, [hostname, editorPath, findSite, findSiteByDomain]);
    // load all site components (don't load if in iframe)
    const { siteComponents, getSiteComponents } = getComponentsApi;
    useEffect(() => void ((site === null || site === void 0 ? void 0 : site.id) && !isIframe() && getSiteComponents(site.id)), [getSiteComponents, site === null || site === void 0 ? void 0 : site.id]);
    // send site components to iframe after loading
    useEffect(() => {
        if (siteComponents.length > 0 && !isIframe()) {
            sendSiteComponents(siteComponents);
        }
    }, [siteComponents, sendSiteComponents]);
    const [isEditingDialog, dialogPlugin] = useMemo(() => {
        var _a;
        if (!selectedRootComponent)
            return [];
        const plugin = (plugins || defaultSitePlugins).find(p => p.type === selectedRootComponent.type);
        return [editing && ((_a = plugin === null || plugin === void 0 ? void 0 : plugin.categories) === null || _a === void 0 ? void 0 : _a.includes('dialog')), plugin];
    }, [editing, plugins, selectedRootComponent]);
    return (React.createElement(SiteThemeProvider, { theme: theme },
        React.createElement(CssBaseline, null),
        React.createElement(UserProvider, null,
            React.createElement(UserContext.Consumer, null, userContext => (React.createElement(EntityProvider, { allowVersioning: true, allowWorkflow: true, hasUser: !!userContext.user },
                React.createElement(QueryProvider, { queryBuilderPlugins: context.queryBuilderPlugins },
                    React.createElement(WorkflowProvider, Object.assign({}, workflowPlugins),
                        React.createElement(SiteContext.Provider, { value: context },
                            React.createElement(Switch, null,
                                React.createElement(Route, { exact: true, path: `${editorPath}login`, component: SiteLogin }),
                                React.createElement(Route, { path: editorPath, component: SiteAdminPage }),
                                React.createElement(Route, null, children)),
                            isEditingDialog &&
                                dialogPlugin &&
                                selectedRootComponent &&
                                isIframe() && (React.createElement(dialogPlugin.component, Object.assign({ open: true, component: selectedRootComponent }, (selectedRootComponent.props || {})))))))))))));
};
//# sourceMappingURL=SiteContext.js.map